import { useState } from 'react';
import { useStore } from '@nanostores/react';
import { Box, Icon, InlineStack, PlainButton, Text } from '@customerio/pluma-components/react';
import { activeSessionIdStore } from '@customerio/hydra-session-state';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useRoleDefinitions } from '@/features/roles';
import { useSampleWorkspace } from '@/features/workspaces';
import { authStore } from '@/services/auth/store';
import { isRoleRedacted } from '@/services/db/role/redaction';
import { useLocation } from '@/services/router/compat';
import { getWorkspaceIdParam } from '@/services/router/workspace-id-param';
import { VIEWER_BANNER_LABEL, VIEWER_BANNER_MESSAGE, VIEWER_BANNER_REDACTED_MESSAGE } from '@/utils/viewer-restricted';
import { dismissViewerBanner, isViewerBannerDismissed } from './dismissal';
import { isViewOnlyWorkspaceRole } from './eligibility';
import * as styles from './index.css';

type ViewerBannerForSessionProps = {
  activeSessionId: number;
  isRedacted: boolean;
  shouldShow: boolean;
};

function ViewerBannerForSession({ activeSessionId, isRedacted, shouldShow }: ViewerBannerForSessionProps) {
  const [isDismissed, setIsDismissed] = useState(() => isViewerBannerDismissed(activeSessionId));
  const isVisible = shouldShow && !isDismissed;
  const message = isRedacted ? VIEWER_BANNER_REDACTED_MESSAGE : VIEWER_BANNER_MESSAGE;

  const handleDismiss = () => {
    dismissViewerBanner(activeSessionId);
    setIsDismissed(true);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Box as="section" role="status" aria-live="polite" aria-atomic="true" className={styles.viewerBanner}>
      <InlineStack alignBlock="center" gap="100" className={styles.content}>
        <Icon name="view-on" size="xs" />
        <Text as="span" text="product-pxs" weight="semibold" className={styles.label}>
          {VIEWER_BANNER_LABEL}
        </Text>
        <Text as="span" text="product-pxs" color="inherit" aria-hidden="true">
          &middot;
        </Text>
        <Text as="span" text="product-pxs" className={styles.message}>
          {message}
        </Text>
      </InlineStack>
      <PlainButton aria-label="Dismiss view-only notice" className={styles.dismissButton} onClick={handleDismiss}>
        <Icon name="close" size="xxs" />
      </PlainButton>
    </Box>
  );
}

export function ViewerBanner() {
  const auth = useStore(authStore);
  const activeSessionId = useStore(activeSessionIdStore);
  const currentWorkspace = useCurrentWorkspace();
  const roleDefinitions = useRoleDefinitions();
  const { isSampleWorkspace } = useSampleWorkspace();
  const location = useLocation();
  const workspaceId = currentWorkspace?.id ?? null;
  const workspaceRole = auth.user?.roles.find((role) => role.workspace_id === workspaceId);
  const isWorkspaceRoute = getWorkspaceIdParam(location.pathname) != null;
  const shouldShow =
    isWorkspaceRoute &&
    auth.account?.is_masq !== true &&
    auth.account?.write_access === true &&
    workspaceId != null &&
    !isSampleWorkspace(workspaceId) &&
    isViewOnlyWorkspaceRole(auth.user?.roles, workspaceId, roleDefinitions);

  return (
    <ViewerBannerForSession
      key={activeSessionId}
      activeSessionId={activeSessionId}
      isRedacted={isRoleRedacted(workspaceRole?.role_name)}
      shouldShow={shouldShow}
    />
  );
}
